import styles from "./Login.module.scss";
import { useState, useEffect } from "react";
import { supabase } from "../../utils/supabaseClient";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

export default function Auth() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordLogin, setIsPasswordLogin] = useState(
    import.meta.env.VITE_DEFAULT_PASSWORD_LOGIN === "true"
  );
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const handleAuthRedirect = async () => {
      if (!window.location.hash) return;

      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get("access_token");
      const type = hashParams.get("type");

      if (!accessToken) return;

      setLoading(true);

      try {
        if (type === "recovery") {
          navigate("/update-password" + window.location.hash, { replace: true });
          return;
        }

        // Let the session be established
        await new Promise((resolve) => setTimeout(resolve, 100));

        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();
        if (error) throw error;

        if (session) {
          // Clear hash but keep pathname
          window.history.replaceState(null, null, window.location.pathname);
          navigate("/", { replace: true });
        }
      } catch (err) {
        console.error("Auth redirect error:", err);
        setError("Error processing login. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    handleAuthRedirect();
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      const returnTo = new URLSearchParams(window.location.search).get("returnTo");
      const from = location.state?.from?.pathname || returnTo || "/";
      const timeoutId = setTimeout(() => {
        navigate(from, { replace: true });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isAuthenticated, navigate, location.state]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isPasswordLogin) {
        const { data, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError) {
          console.error("Sign in error:", signInError);
          setError(signInError.message);
        } else if (data?.session) {
          const returnTo = new URLSearchParams(window.location.search).get("returnTo");
          const from = location.state?.from?.pathname || returnTo || "/";
          navigate(from, { replace: true });
        }
      } else {
        // Magic link flow
        const { error: signInError } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/login`,
            shouldCreateUser: true,
            data: {
              redirect_to: location.state?.from?.pathname || "/",
            },
          },
        });

        if (signInError) {
          setError(signInError.message);
        } else {
          alert("Check your email for the login link!");
        }
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className={styles.flexCenter}>Loading...</div>;
  }

  return (
    <div className={`row ${styles.flexCenter}`}>
      <div className={`${styles.col6} ${styles.formWidget}`}>
        <h1 className={styles.header}>Ingresa a Papo</h1>
        <article className={styles.description}>
          <p className={styles.description}>
            {isPasswordLogin
              ? "Ingresa con tu correo y contraseña"
              : "Si ya tienes cuenta, ingresa tu correo y recibirás un link para iniciar sesión"}
          </p>
        </article>
        {error && <div className={styles.error}>{error}</div>}
        <form className={styles.formWidget} onSubmit={handleLogin}>
          <div>
            <input
              className={styles.inputField}
              type="email"
              placeholder="Email"
              value={email}
              required={true}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {isPasswordLogin && (
            <div className={styles.passwordSection}>
              <input
                className={styles.inputField}
                type="password"
                placeholder="Password"
                value={password}
                required={isPasswordLogin}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Link to="/password-recovery" className={styles.forgotPassword}>
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          )}
          <div>
            <button className={styles.button} disabled={loading}>
              {loading ? (
                <span>Loading</span>
              ) : (
                <span>{isPasswordLogin ? "Iniciar sesión" : "Enviar link"}</span>
              )}
            </button>
          </div>
        </form>
        <button
          className={styles.switchButton}
          onClick={() => setIsPasswordLogin(!isPasswordLogin)}
        >
          {isPasswordLogin ? "Usar login con link mágico" : "Usar login con contraseña"}
        </button>
      </div>
    </div>
  );
}
